import { usePathname } from 'next/navigation';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useMemo } from 'react';

import { ProgressStep } from '@Components/design-system/progress';
import { IQueryData } from '@Reducers/common';
import { IUserData } from '@Reducers/user';
import { useAppSelector } from '@Redux/hooks';
import { CONSULTATION_TYPE, DEFAULT_LOCATION_CODE, INSTANT_LOCATION_CODE } from '@Utils/constants';
import { FEATURE_FLAGS } from '@Utils/feature-flags';
import { formatName, isOfflineLocation } from '@Utils/helper';

import { Direction } from '../constants';
import { FLOW_STEP_CONFIG, FlowKind } from './type';

const useComputeSteps = (): ProgressStep[] => {
  const queryData: IQueryData = useAppSelector((state) => state.common.queryData);
  const userData: IUserData = useAppSelector((state) => state.user.userData);
  const pathname = usePathname().split('/')[2];
  const isDiagnosisExperiment = useFeatureFlagEnabled(FEATURE_FLAGS.DIAGNOSIS_EXPERIMENT);

  const steps = useMemo(() => {
    if (!pathname || !userData?.id) return [];
    //show language if the user has no therapist or physician assigned
    const showLanguage =
      queryData?.locationCode === DEFAULT_LOCATION_CODE &&
      ((queryData?.consultationCode === CONSULTATION_TYPE.TH &&
        !userData?.therapistId &&
        !queryData?.providerId?.[0]) ||
        (queryData?.consultationCode !== CONSULTATION_TYPE.TH &&
          !userData?.physicianId &&
          !queryData?.providerId?.[0]));

    const isOfflineFlow = isOfflineLocation(queryData?.locationCode);
    const isInstantFlow = queryData?.locationCode === INSTANT_LOCATION_CODE;

    const flowKind = sessionStorage.getItem('flow-kind');
    const customFlowKind =
      isDiagnosisExperiment &&
      [FlowKind.SPECIFIC_LOCATION, FlowKind.DEFAULT].includes(flowKind as FlowKind) &&
      FlowKind.DIANOSIS_EXPERIMENT;
    const steps = FLOW_STEP_CONFIG[customFlowKind || flowKind || FlowKind.DEFAULT];
    switch (true) {
      case flowKind === FlowKind.ASSESSMENT_REPORT:
        return FLOW_STEP_CONFIG[FlowKind.ASSESSMENT_REPORT];
      case isInstantFlow:
        return FLOW_STEP_CONFIG[FlowKind.INSTANT];
      case flowKind === FlowKind.LAB && !Boolean(userData?.verifiedName):
        return FLOW_STEP_CONFIG[FlowKind.LAB];
      case flowKind === FlowKind.LAB && Boolean(userData?.verifiedName):
        return FLOW_STEP_CONFIG[FlowKind.LAB].filter((step: Direction) => step !== Direction.PERSONAL_DETAILS);
      default:
        return steps.filter((step: Direction) =>
          step === Direction.SELECT_LANGUAGE ? showLanguage : step === Direction.SELECT_CLINIC ? isOfflineFlow : true,
        );
    }
  }, [queryData, userData, pathname, isDiagnosisExperiment]);

  return steps.map((step) => ({
    label: formatName(step),
    isActive: pathname?.includes(step),
  }));
};

export default useComputeSteps;
